import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Breadcrumbs from '../components/Breadcrumbs';

const NamespaceDetailsPage = () => {
    const { clusterName, namespace } = useParams();
    const [namespaceInfo, setNamespaceInfo] = useState<any>(null);
    const [pods, setPods] = useState<any[]>([]);
    const [services, setServices] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchNamespaceData = async () => {
            setLoading(true);
            try {
                // Fetch namespace info
                const nsRes = await axios.get(`/api/monitoring/clusters/${clusterName}/namespaces`);
                const ns = nsRes.data.data.find((n: any) => n.name === namespace);
                setNamespaceInfo(ns);
                // Fetch pods in this namespace
                const podsRes = await axios.get(`/api/monitoring/clusters/${clusterName}/namespaces/${namespace}/pods`);
                setPods(podsRes.data.data);
                // Fetch services in this namespace
                const svcRes = await axios.get(`/api/monitoring/clusters/${clusterName}/namespaces/${namespace}/services`);
                setServices(svcRes.data.data);
            } catch (err) {
                setNamespaceInfo(null);
                setPods([]);
                setServices([]);
            }
            setLoading(false);
        };
        fetchNamespaceData();
    }, [clusterName, namespace]);

    // Filter namespaces by cluster if clusterName param is present
    const filteredNamespaces = namespaceInfo ? [namespaceInfo] : [];

    const filteredPods = pods.filter((pod: any) =>
        pod.name.toLowerCase().includes(filter.toLowerCase())
    );
    const filteredServices = services.filter((svc: any) =>
        svc.name.toLowerCase().includes(filter.toLowerCase())
    );

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading namespace details...</span>
                </div>
            </div>
        );
    }
    if (!namespaceInfo) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <p className="text-yellow-800 dark:text-yellow-200">Namespace not found</p>
                </div>
            </div>
        );
    }
    return (
        <div className="container mx-auto px-4 py-6">
            <Breadcrumbs />
            <div className="flex items-center mb-6">
                <Link to={`/kubernetes/clusters/${clusterName}/details`} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mr-4">
                    ? Back to Cluster
                </Link>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{namespaceInfo.name}</h1>
                <span className={`ml-4 px-3 py-1 text-sm font-semibold rounded-full ${namespaceInfo.status === 'Active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                    {namespaceInfo.status}
                </span>
            </div>
            <div className="mb-6">
                <input
                    type="text"
                    placeholder="Filter pods/services..."
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                />
            </div>
            {/* Pods Table */}
            <div className="mb-8">
                <h2 className="text-lg font-semibold mb-2">Pods</h2>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Node</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Restarts</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Age</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredPods.map((pod: any, idx: number) => (
                                <tr
                                    key={pod.name + '-' + idx}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                    onClick={() => navigate(`/kubernetes/pods/${clusterName}/${namespace}/${pod.name}`)}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">{pod.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{pod.status}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{pod.nodeName || '�'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{pod.restartCount ?? '�'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{pod.age || '�'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredPods.length === 0 && (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            {filter ? 'No pods match your filter' : 'No pods found'}
                        </div>
                    )}
                </div>
            </div>
            {/* Services Table */}
            <div>
                <h2 className="text-lg font-semibold mb-2">Services</h2>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">ClusterIP</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ports</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredServices.map((svc: any, idx: number) => (
                                <tr
                                    key={svc.name + '-' + idx}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                    onClick={() => navigate(`/kubernetes/services/${clusterName}/${namespace}/${svc.name}`)}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">{svc.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{svc.type}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{svc.clusterIP || '�'}</td>
                                    <td className="px-6 py-4 text-gray-500 dark:text-gray-400">
                                        {svc.ports && svc.ports.length > 0 ? (
                                            <div className="space-y-1">
                                                {svc.ports.map((port: any, pidx: number) => (
                                                    <div key={pidx}>
                                                        {port.port}{port.targetPort ? `:${port.targetPort}` : ''}/{port.protocol}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : '�'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredServices.length === 0 && (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            {filter ? 'No services match your filter' : 'No services found'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NamespaceDetailsPage;
